import React, { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { NewsArticleJsonLd } from 'next-seo';
import ImageViewer from 'react-simple-image-viewer';
import { useMediaQuery, useTheme } from '@mui/material';

// Components
import CarouselNewsImages from '../../components/carousel/carousel-news-images/CarouselNewsImages';

// Utils
import { getStrapiMedia } from '../../utils/media';
import { hexToRgb, rgbDataURL } from 'corporate-utils';
import { mapToNewsArticleJsonLd } from '../../utils/schemas';

// Models
import { PageBreadcrumb } from '../../models/interface';

// Styles
import {
  BottomImagesWrapper,
  CarouselWrapper,
  Category,
  ContentDivision,
  CoverContainer,
  ImageContainerDefaultStyled,
  ImageContainerStyled,
  ImageViewerWrapper,
  MarkdownWrapper,
  OverlayStyled,
  SectionHeaderContainer,
  StyledContentWrapper,
  StyledMarkdown,
  StyledVideo,
  Title,
  CoverWrapper,
} from './SingleNews.style';
import {
  HeaderDescriptionPositionType,
  HeaderCoverResizeType,
} from '../../models/enum';
import { Newsa } from '../../models/auto-generated-strapi-types/types/Newsa';
import { Media } from '../../models/auto-generated-strapi-types/types/Media';
import { correctHeightProportion } from '../../utils/image';
import { SingleContentWrapperMobile } from '../../styles/shared';
import { DEFAULT_IMAGE } from '../../models/domain/const';
import ImageWithFallback from '../../components/image-with-fallback/ImageWithFallback';
import SectionHeaderCms from '../../components/section-header-cms/SectionHeaderCms';
import { Companies } from '../../models/domain/enum';

const SingleNews: React.FC<any> = ({
  data,
  translations,
  metadata,
  isPrerenderRequest,
}: {
  data: any;
  translations: any;
  metadata: any;
  isPrerenderRequest: boolean;
}) => {
  const [isViewerOpen, setIsViewerOpen] = useState<boolean>(false);
  const [viewerCurrentIndex, setViewerCurrentIndex] = useState<number>(0);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpointsMediaQuery.up('md'));
  const [, setRerender] = useState<number>(0); // Trick to force re-render
  const news: Newsa = data?.news?.data?.[0] ?? {};
  const { locale } = useRouter();

  const breadCrumb: PageBreadcrumb = {
    links: [
      {
        href: `/${locale}`,
        name: translations?.urls?.home,
      },
      {
        href: `${translations?.mainLinks?.[1]?.subLink?.[0]?.url}`,
        name:
          translations?.mainLinks?.[1]?.subLink?.[0]?.text ||
          translations?.urls?.newsAndEvents,
      },
    ],
    pageActive: {
      name:
        news?.attributes?.title ||
        news?.attributes?.category ||
        translations?.pages?.newsEvents?.singleNews?.news,
    },
    lightContrast: false,
  };
  const overlayColor = news?.attributes?.overlayColor;

  const openImageViewer = (index = 0): void => {
    setViewerCurrentIndex(index);
    setIsViewerOpen(!isViewerOpen);
  };

  const image = useMemo(
    () =>
      typeof window === 'undefined' || isDesktop
        ? news?.attributes?.coverImage
        : news?.attributes?.mobileCoverImage?.data
          ? news?.attributes?.mobileCoverImage
          : news?.attributes?.coverImage,
    [
      isDesktop,
      news?.attributes?.coverImage,
      news?.attributes?.mobileCoverImage,
    ]
  );

  const video = useMemo(
    () =>
      typeof window === 'undefined' || isDesktop
        ? news?.attributes?.video
        : news?.attributes?.mobileVideo?.data
          ? news?.attributes?.mobileVideo
          : news?.attributes?.video,
    [isDesktop, news?.attributes?.mobileVideo, news?.attributes?.video]
  );

  const noImage =
    !(image && image.data && image.data.attributes) &&
    !!(video && video.data && video.data.attributes);
  const {
    titleFontSize,
    titleMobileFontSize,
    titleMobileFontWeight,
    titleFontWeight,
    headerDescriptionPosition,
    headerCoverResize,
    forceDescriptionToBeBlack,
    forceDescriptionToBeWhite,
    forceHideDate,
    forceHideCategory,
    forceHideTitle,
    forceHideBreadcrumb,
    videoAutoPlay,
    videoControls,
    videoLoop,
    videoMuted,
    coverTextColor,
    shadowBehindText,
  } = news?.attributes || {};

  const atLeastSomethingInDescriptionIsTrue = () => {
    return (
      !forceHideDate ||
      !forceHideCategory ||
      !forceHideTitle ||
      !forceHideBreadcrumb
    );
  };

  const categoryFieldData = !forceHideCategory
    ? [
      news?.attributes?.category ||
          translations?.pages?.newsEvents?.singleNews?.news,
    ]
    : [];
  if (!forceHideCategory) {
    news?.attributes?.types?.data
      ?.filter((type) => type?.attributes?.type)
      .map((type) => {
        categoryFieldData.push(type?.attributes?.type);
      });
  }
  if (!forceHideDate) {
    categoryFieldData.push(news?.attributes?.date);
  }

  useEffect(() => {
    setRerender(Date.now());
  }, []);

  // https://github.com/vercel/next.js/blob/v12.2.3/docs/basic-features/image-optimization.md
  return (
    <StyledContentWrapper>
      {/* Image and description */}
      <CoverContainer headerCoverResize={headerCoverResize}>
        {/* COVER IMAGE */}
        {/* When image is cover */}
        <ImageContainerDefaultStyled headerCoverResize={headerCoverResize}>
          {image &&
            image?.data &&
            image?.data?.attributes &&
            (HeaderCoverResizeType.DEFAULT_COVER ===
              (headerCoverResize as HeaderCoverResizeType) ||
              !headerCoverResize) && (
            <>
              {video?.data ? (
                <StyledVideo
                  autoPlay={videoAutoPlay || false}
                  controls={videoControls || false}
                  loop={videoLoop || false}
                  muted={videoMuted || false}
                  objectFit={'cover'}
                >
                  <source src={getStrapiMedia(video.data?.attributes?.url)} />
                </StyledVideo>
              ) : (
                <ImageWithFallback
                  isPrerenderRequest={isPrerenderRequest}
                  layout="fill"
                  objectFit="cover"
                  placeholder="blur"
                  blurDataURL={
                    overlayColor
                      ? rgbDataURL(
                        hexToRgb(overlayColor)?.[0],
                        hexToRgb(overlayColor)?.[1],
                        hexToRgb(overlayColor)?.[2]
                      )
                      : rgbDataURL(
                        hexToRgb('ffffff')?.[0],
                        hexToRgb('ffffff')?.[1],
                        hexToRgb('ffffff')?.[2]
                      )
                  }
                  src={getStrapiMedia(image?.data?.attributes?.url)}
                  alt={image?.data?.attributes?.alternativeText || ''}
                  fallbackSrc={DEFAULT_IMAGE}
                />
              )}
            </>
          )}
        </ImageContainerDefaultStyled>

        {/* COVER IMAGE */}
        {/* When image is contain */}
        <ImageContainerStyled>
          {image &&
            image?.data &&
            image?.data?.attributes &&
            (HeaderCoverResizeType.CONTAIN ===
              (headerCoverResize as HeaderCoverResizeType) ||
              !headerCoverResize) && (
            <>
              {video?.data ? (
                <StyledVideo
                  autoPlay={videoAutoPlay || false}
                  controls={videoControls || false}
                  loop={videoLoop || false}
                  muted={videoMuted || false}
                  objectFit={'contain'}
                >
                  <source src={getStrapiMedia(video.data?.attributes?.url)} />
                </StyledVideo>
              ) : (
                <ImageWithFallback
                  isPrerenderRequest={isPrerenderRequest}
                  width={'1920'}
                  height={
                    correctHeightProportion(
                      image?.data?.attributes?.height,
                      image?.data?.attributes?.width
                    ) || '600'
                  }
                  placeholder="blur"
                  blurDataURL={
                    overlayColor
                      ? rgbDataURL(
                        hexToRgb(overlayColor)?.[0],
                        hexToRgb(overlayColor)?.[1],
                        hexToRgb(overlayColor)?.[2]
                      )
                      : rgbDataURL(
                        hexToRgb('ffffff')?.[0],
                        hexToRgb('ffffff')?.[1],
                        hexToRgb('ffffff')?.[2]
                      )
                  }
                  src={getStrapiMedia(image?.data?.attributes?.url || '')}
                  alt={image?.data?.attributes?.alternativeText || ''}
                  fallbackSrc={DEFAULT_IMAGE}
                />
              )}
            </>
          )}
        </ImageContainerStyled>
        {/* Overlay cover */}
        {data?.company !== Companies.FELSINEA ||
          (!data?.company &&
            ((typeof window === 'undefined' || isDesktop) && overlayColor ? (
              <OverlayStyled overlay={overlayColor}></OverlayStyled>
            ) : (
              <></>
            )))}

        {/* Description inside image */}
        {data?.company !== Companies.FELSINEA || !data?.company ? (
          HeaderDescriptionPositionType.DEFAULT ===
            (headerDescriptionPosition as HeaderDescriptionPositionType) &&
          atLeastSomethingInDescriptionIsTrue() ? (
              <CoverWrapper lightContrast={true && !forceDescriptionToBeBlack}>
                <ContentDivision shadowBehindText={!!shadowBehindText}>
                  {!forceHideTitle && (
                    <Title
                      fontSize={titleFontSize}
                      fontMobileSize={titleMobileFontSize}
                      fontWeight={titleFontWeight}
                      fontMobileWeight={titleMobileFontWeight}
                      coverTextColor={coverTextColor}
                    >
                      <StyledMarkdown>{news?.attributes?.title}</StyledMarkdown>
                    </Title>
                  )}
                  {categoryFieldData && (
                    <Category coverTextColor={coverTextColor}>
                      {categoryFieldData?.filter(Boolean).join(' | ')}
                    </Category>
                  )}
                </ContentDivision>
              </CoverWrapper>
            ) : (
              <></>
            )
        ) : (
          <></>
        )}
      </CoverContainer>

      {/* Description under the image */}
      {data?.company !== Companies.FELSINEA || !data?.company ? (
        HeaderDescriptionPositionType.UNDER ===
          (headerDescriptionPosition as HeaderDescriptionPositionType) &&
        atLeastSomethingInDescriptionIsTrue() ? (
            <MarkdownWrapper lightContrast={false || forceDescriptionToBeWhite}>
              <ContentDivision maxDimension={true}>
                {HeaderDescriptionPositionType.UNDER ===
              (headerDescriptionPosition as HeaderDescriptionPositionType) ? (
                    <>
                      {!forceHideTitle && (
                        <Title
                          lightContrast={noImage}
                          fontSize={titleFontSize}
                          fontMobileSize={titleMobileFontSize}
                          fontWeight={titleFontWeight}
                          fontMobileWeight={titleMobileFontWeight}
                          coverTextColor={coverTextColor}
                        >
                          <StyledMarkdown>{news?.attributes?.title}</StyledMarkdown>
                        </Title>
                      )}
                      {categoryFieldData && (
                        <Category coverTextColor={coverTextColor}>
                          {categoryFieldData?.filter(Boolean).join(' | ')}
                        </Category>
                      )}
                    </>
                  ) : (
                    <></>
                  )}
              </ContentDivision>
            </MarkdownWrapper>
          ) : (
            <></>
          )
      ) : (
        <></>
      )}

      {data?.sectionHeader && data?.company === Companies.FELSINEA && (
        <SectionHeaderContainer>
          <SectionHeaderCms
            id={data?.id}
            description={data?.sectionHeader?.description}
            personalization={data?.sectionHeader?.personalization}
            mainTitle={data?.sectionHeader?.mainTitle}
            title={data?.sectionHeader?.title}
            backgroundColor={data?.sectionHeader?.backgroundColor}
            topIcon={data?.sectionHeader?.topIcon}
            isPrerenderRequest={isPrerenderRequest}
          />
        </SectionHeaderContainer>
      )}
      {/* Text from markdown */}
      <SingleContentWrapperMobile>
        <MarkdownWrapper>
          <StyledMarkdown>{news?.attributes?.text ?? ''}</StyledMarkdown>
        </MarkdownWrapper>
      </SingleContentWrapperMobile>

      {news?.attributes?.bottomImages?.data && (
        <>
          {(typeof window === 'undefined' || isDesktop) &&
            news?.attributes?.bottomImages?.data && (
            <BottomImagesWrapper>
              {React.Children.toArray(
                news?.attributes?.bottomImages?.data?.map?.(
                  (image: Media, index: number) => (
                    <ImageWithFallback
                      isPrerenderRequest={isPrerenderRequest}
                      src={getStrapiMedia(image?.attributes?.url ?? '')}
                      alt={image?.attributes?.alternativeText ?? ''}
                      layout="intrinsic"
                      width={image?.attributes?.width}
                      height={image?.attributes?.height}
                      objectFit="contain"
                      onClick={() => openImageViewer(index)}
                      fallbackSrc={DEFAULT_IMAGE}
                    />
                  )
                )
              )}
            </BottomImagesWrapper>
          )}
          {typeof window !== 'undefined' && !isDesktop && (
            <CarouselWrapper>
              <CarouselNewsImages
                useImageWrapper
                onImageClick={openImageViewer}
                images={news?.attributes?.bottomImages?.data}
                isPrerenderRequest={isPrerenderRequest}
              />
            </CarouselWrapper>
          )}
          {isViewerOpen && (
            <ImageViewerWrapper>
              <ImageViewer
                src={news?.attributes?.bottomImages?.data?.map(
                  (image: Media) => {
                    return image?.attributes?.url;
                  }
                )}
                currentIndex={viewerCurrentIndex}
                onClose={openImageViewer}
                disableScroll={false}
                closeOnClickOutside={true}
              />
            </ImageViewerWrapper>
          )}
        </>
      )}

      {/* SEO information */}
      <NewsArticleJsonLd
        {...mapToNewsArticleJsonLd(
          news,
          breadCrumb,
          metadata,
          translations,
          locale
        )}
      />
    </StyledContentWrapper>
  );
};

export default SingleNews;
